/**
 * Supabase Client for Frontend Authentication
 *
 * This module provides a Supabase client instance configured for
 * browser-based authentication using Supabase Auth.
 *
 * Part of the Supabase Auth migration (Phase 1).
 */

import { createClient } from '@supabase/supabase-js';

// Supabase project configuration
// These should match your backend Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Check if Supabase is configured
const isSupabaseConfigured = !!(supabaseUrl && supabaseAnonKey);

// Note: If Supabase is not configured, new auth features are disabled but legacy auth continues to work.
// To enable Supabase Auth, set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.

// Create Supabase client instance (null if not configured)
export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        // Auto refresh tokens
        autoRefreshToken: true,
        // Persist session in local storage
        persistSession: true,
        // Detect session from URL (for password reset links)
        detectSessionInUrl: true,
        // Use PKCE flow for enhanced security
        flowType: 'pkce',
      },
    })
  : null;

/**
 * Get current Supabase session
 * @returns {Promise<Session|null>}
 */
export const getCurrentSession = async () => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session;
};

/**
 * Get current Supabase user
 * @returns {Promise<User|null>}
 */
export const getCurrentUser = async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user;
};

/**
 * Sign out from Supabase Auth
 * @returns {Promise<void>}
 */
export const signOut = async () => {
  await supabase.auth.signOut();
  // Also clear any custom tokens from localStorage
  localStorage.removeItem('access_token');
};

export default supabase;
